import React, {Component} from "react"
import {connect} from "react-redux"
import {find} from "lodash"
import CopyToClipboard from "react-copy-to-clipboard"

import {ReduxState} from "@irati/store/types"
import {Body} from "@irati/ui/src/Typography"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import medicalTracking from "@irati-tracking/medical"
import {MedicalDCAUser} from "@irati/medical/types"
import ShowPopUp from "@irati/store/actions/ShowSuccessPopUp"
import onboardingService, {OnboardingStep} from "@irati/onboarding/services/onboarding"
import AuthActions from "@irati/auth/store/actions/AuthActions"
import {User} from "@irati/types"
import TooltipType from "@irati/ui/src/TooltipNotification/types"
import isMobileDevice from "@irati/utils/mobile"

import {ACTIVATION_CODE} from "../../../constants/tracking"
import ActivationSteps from "../../../components/ActivationSteps"
import AppFeatures from "../../../components/AppFeatures"
import ContinueOnline from "../../../components/ContinueOnline"
import StepPage from "../../../components/StepPage"
import {TextMeInstructions} from "../../../components/shared"
import appScreen from "../../../images/appScreen.jpg"
import {Flex, CodeWrapper, Code, AppScreenImg, Description, TextMeInstructionsWrapper, TooltipAttach} from "./styled"

interface Props extends InjectIntl {
  user: MedicalDCAUser
  isTablet: boolean
  showPopUp: (tooltip: TooltipType) => void
  updateUser: (user: User) => void
}

const TOOLTIP_ACTIVATION_CODE = "activationCode"

class ActivationCode extends Component<Props> {
  popupTimeout = null

  constructor(props: Props) {
    super(props)
    medicalTracking.viewStep(3, props.user.activationCode)
  }

  componentDidMount() {
    const {
      isTablet,
      showPopUp,
      auth: {onboarding}
    } = this.props

    window.scrollTo(0, 0)

    const hasViewedMedicalActivationPage = find(
      onboarding.completedSteps,
      (step) => step === OnboardingStep.HAS_VIEWED_MEDICAL_ACTIVATION_PAGE
    )
    const hasHiddenMedicalActivationPageTooltip = find(
      onboarding.completedSteps,
      (step) => step === OnboardingStep.HAS_HIDDEN_MEDICAL_ACTIVATION_PAGE
    )

    if (!isTablet) {
      if (hasViewedMedicalActivationPage && !hasHiddenMedicalActivationPageTooltip) {
        const {formatMessage} = this.props

        const tooltip = {
          show: true,
          text: formatMessage({id: "medical.code.tooltip.text"}),
          subtext: formatMessage({id: "medical.tooltip.subtext"}),
          anchor: TOOLTIP_ACTIVATION_CODE,
          positionVertically: true,
          height: 220,
          leftTriangle: true,
          onChange: () => this.completeStep(OnboardingStep.HAS_HIDDEN_MEDICAL_ACTIVATION_PAGE)
        }
        showPopUp(tooltip)
        this.popupTimeout = setTimeout(() => {
          showPopUp({show: false})
        }, 10000)
      } else {
        this.completeStep(OnboardingStep.HAS_VIEWED_MEDICAL_ACTIVATION_PAGE)
      }
    }
  }

  componentWillUnmount() {
    if (this.popupTimeout) {
      clearTimeout(this.popupTimeout)
    }
    this.props.showPopUp({show: false})
  }

  componentDidUpdate() {
    const {isTablet, showPopUp} = this.props
    if (isTablet) {
      showPopUp({show: false})
    }
  }

  completeStep = (step) => {
    const {auth} = this.props
    onboardingService.completeStep(auth.user, step, auth.accessToken).then((onboarding) => {
      this.props.updateAuth(auth.user, onboarding)
    })
  }

  handleContinueOnline = () => {
    medicalTracking.continueOnline(this.props.user.activationCode, ACTIVATION_CODE)
  }

  render() {
    const isMobile = !!isMobileDevice()
    const {
      user: {activationCode, phoneNumber}
    } = this.props

    return (
      <StepPage currentStep={3} noPaddingBottom>
        {!isMobile && (
          <Description>
            <FormattedMessage id="medical.activation.desktopStep.desc" />
          </Description>
        )}
        <ActivationSteps isMobile={isMobile} />
        <CodeWrapper>
          <Code>
            {isMobile ? (
              <CopyToClipboard text={activationCode}>
                <Body large maxWidth="100%" marginBottom={0} weight="bold">
                  {activationCode}
                </Body>
              </CopyToClipboard>
            ) : (
              <Body large maxWidth="100%" marginBottom={0} weight="bold">
                {activationCode}
              </Body>
            )}
            <TooltipAttach data-attach-tooltip={TOOLTIP_ACTIVATION_CODE} />
          </Code>
        </CodeWrapper>
        <Flex>
          <div>
            <AppFeatures />
            <TextMeInstructionsWrapper>
              <TextMeInstructions phoneNumber={phoneNumber} activationCode={activationCode} trackingLocation={ACTIVATION_CODE} />
            </TextMeInstructionsWrapper>
            <ContinueOnline
              onClick={this.handleContinueOnline}
              activationCode={activationCode}
              link="medical.code.continueOnline.link.href"
              text="medical.code.continueOnline.link.text"
              isGrey
              isMarginLarge
            />
          </div>
          <AppScreenImg src={appScreen} />
        </Flex>
      </StepPage>
    )
  }
}

const mapStateToProps = (state: ReduxState) => {
  return {
    isTablet: state.mq.lessThan.large,
    auth: state.auth
  }
}

const mapDispatchToProps = (dispatch: (action: {type: string}) => void) => ({
  showPopUp: (options) => dispatch(ShowPopUp(options)),
  updateAuth: (user, onboarding) => dispatch(AuthActions.UpdateAuthAction({user, onboarding}))
})

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ActivationCode))
